import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { promoCodeService } from 'components/api/promoCodes';
import {
  PromoCode,
  PromoCodesParams,
  PromoCodesResponse,
  CreatePromoCodeRequest,
} from 'types/promoCode';

interface PromoCodesState {
  data: PromoCode[];
  total: number;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  params: PromoCodesParams;
}

const initialState: PromoCodesState = {
  data: [],
  total: 0,
  isLoading: false,
  isCreating: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    siteId: '',
  },
};

export const fetchPromoCodes = createAsyncThunk(
  'promoCodes/fetchPromoCodes',
  async (params: PromoCodesParams, { rejectWithValue }) => {
    try {
      const response = await promoCodeService.getPromoCodes(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch promo codes',
      );
    }
  },
);

export const createPromoCode = createAsyncThunk(
  'promoCodes/createPromoCode',
  async (data: CreatePromoCodeRequest, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await promoCodeService.createPromoCode(data);
      const { promoCodes } = getState() as { promoCodes: PromoCodesState };
      dispatch(fetchPromoCodes(promoCodes.params));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to create promo code',
      );
    }
  },
);

export const updatePromoCode = createAsyncThunk(
  'promoCodes/updatePromoCode',
  async (
    { id, data }: { id: string; data: Partial<CreatePromoCodeRequest> },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const response = await promoCodeService.updatePromoCode(id, data);
      const { promoCodes } = getState() as { promoCodes: PromoCodesState };
      dispatch(fetchPromoCodes(promoCodes.params));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to update promo code',
      );
    }
  },
);

export const deletePromoCode = createAsyncThunk(
  'promoCodes/deletePromoCode',
  async (id: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await promoCodeService.deletePromoCode(id);
      const { promoCodes } = getState() as { promoCodes: PromoCodesState };
      dispatch(fetchPromoCodes(promoCodes.params));
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to delete promo code',
      );
    }
  },
);

const promoCodesSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<PromoCodesParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    clearData: (state) => {
      state.data = [];
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromoCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action: PayloadAction<PromoCodesResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data?.docs || [];
        state.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createPromoCode.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createPromoCode.fulfilled, (state) => {
        state.isCreating = false;
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      });
  },
});

export const { setParams, clearData } = promoCodesSlice.actions;
export default promoCodesSlice.reducer;
